import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ScrollText } from "lucide-react";
import {
  Button,
  DetailSection,
  EmptyState,
  ListCard,
  SkeletonList,
  SkeletonText,
  StatusBadge,
  Textarea,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { PortalApiError } from "../api/client";
import {
  tpsDebtCollectorLanguageApi,
  type TpsDebtCollectorLanguage,
  type TpsDebtCollectorLanguageStatus,
} from "../api/tpsDebtCollectorLanguage";
import { US_STATES, usStateName } from "../lib/usStates";

// TPS Servicing Portal PR-D — the firm's "Debt-Collector Language" surface.
// The servicer authors a borrower-facing debt-collector disclosure per US
// state: Phoenix primes a default, the firm reviews it with counsel, then
// approves it. An approved disclosure for the borrower's state is required
// before the firm can accept a loan for that state (the Loans tab deep-links a
// blocked accept here via ?state=). No React Query in the portal — load state
// is plain useState, and Save/Approve re-run the list loader the caller owns.

// FE-only banner shown ABOVE the editor. It is NEVER sent to the server and is
// NOT part of bodyText — the firm's stored disclosure stays clean.
const EDITOR_BANNER = "THIS IS AN EXAMPLE. REVIEW WITH COUNSEL BEFORE APPROVING.";

const STATUS_TONE: Record<TpsDebtCollectorLanguageStatus, StatusTone> = {
  Approved: "ok",
  Draft: "warn",
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

function stateLabel(code: string): string {
  const name = usStateName(code);
  return name === code ? code : `${name} (${code})`;
}

export function TpsDebtCollectorLanguageTab() {
  const [rows, setRows] = useState<TpsDebtCollectorLanguage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openState = params.get("state");

  const loadList = useCallback(async () => {
    const data = await tpsDebtCollectorLanguageApi.list();
    setRows(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsDebtCollectorLanguageApi.list();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your debt-collector language."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpenState = (code: string | null) => {
    const updated = new URLSearchParams(params);
    if (code) updated.set("state", code);
    else updated.delete("state");
    setParams(updated, { replace: true });
  };

  // Patch the current (or newly authored) row into the list so "Back to
  // states" reflects a save/approve without a refetch round-trip.
  const patchRow = (updated: TpsDebtCollectorLanguage) => {
    setRows((prev) => {
      if (!prev) return prev;
      const exists = prev.some((r) => r.stateCode === updated.stateCode);
      const next = exists
        ? prev.map((r) => (r.stateCode === updated.stateCode ? updated : r))
        : [...prev, updated];
      return next.sort((a, b) => a.stateCode.localeCompare(b.stateCode));
    });
  };

  if (openState) {
    return (
      <DebtCollectorLanguageEditor
        stateCode={openState}
        onBack={() => setOpenState(null)}
        onSaved={patchRow}
        refreshList={loadList}
      />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  if (!rows) {
    return <SkeletonList rows={4} data-testid="tps-dcl-loading" />;
  }

  return (
    <section aria-label="Debt-collector language" className="flex flex-col gap-space-4">
      <AddStatePicker authoredCodes={rows.map((r) => r.stateCode)} onAdd={setOpenState} />

      {rows.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No states authored yet"
          description="Add a state above to review the primed debt-collector disclosure with counsel and approve it. You'll need an approved disclosure for a borrower's state before you can accept their loan."
        />
      ) : (
        <div className="flex flex-col gap-space-3">
          {rows.map((row) => (
            <ListCard
              key={row.stateCode}
              data-testid={`tps-dcl-row-${row.stateCode}`}
              title={stateLabel(row.stateCode)}
              status={
                <StatusBadge tone={STATUS_TONE[row.status]}>{row.status}</StatusBadge>
              }
              meta={
                <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
                  <span>Version {row.version}</span>
                  {row.isApproved && row.approvedAt && (
                    <>
                      <span aria-hidden>·</span>
                      <span>
                        Approved <Timestamp value={row.approvedAt} />
                      </span>
                    </>
                  )}
                </span>
              }
              actions={
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setOpenState(row.stateCode)}
                  data-testid={`tps-dcl-open-${row.stateCode}`}
                >
                  {row.isApproved ? "Review" : "Edit"}
                </Button>
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

function AddStatePicker({
  authoredCodes,
  onAdd,
}: {
  authoredCodes: string[];
  onAdd: (code: string) => void;
}) {
  const [value, setValue] = useState("");
  const authored = useMemo(() => new Set(authoredCodes), [authoredCodes]);

  return (
    <div className="flex flex-wrap items-end gap-space-3">
      <label className="flex flex-col gap-space-1">
        <span className="text-caption text-gray-mid uppercase tracking-label">Add a state</span>
        <select
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label="Add a state"
          data-testid="tps-dcl-add-state"
          className="h-10 appearance-none rounded-md border border-border-light bg-white-pure px-space-3 text-small text-onyx focus-visible:outline-none focus-visible:shadow-goldRing"
        >
          <option value="">Select a state…</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name} ({s.code}){authored.has(s.code) ? " — authored" : ""}
            </option>
          ))}
        </select>
      </label>
      <Button
        type="button"
        variant="primary"
        size="sm"
        disabled={!value}
        onClick={() => {
          if (value) onAdd(value);
        }}
        data-testid="tps-dcl-add-open"
      >
        Open editor
      </Button>
    </div>
  );
}

function DebtCollectorLanguageEditor({
  stateCode,
  onBack,
  onSaved,
  refreshList,
}: {
  stateCode: string;
  onBack: () => void;
  onSaved: (updated: TpsDebtCollectorLanguage) => void;
  refreshList: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [row, setRow] = useState<TpsDebtCollectorLanguage | null>(null);
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // GET lazily creates a Draft seeded with the primed default if none
        // exists yet for this state.
        const data = await tpsDebtCollectorLanguageApi.get(stateCode);
        if (!cancelled) {
          setRow(data);
          setBody(data.bodyText);
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load this state's disclosure."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stateCode]);

  const apply = useCallback(
    (updated: TpsDebtCollectorLanguage) => {
      setRow(updated);
      setBody(updated.bodyText);
      onSaved(updated);
    },
    [onSaved],
  );

  const save = useSubmittingMutation(
    () => tpsDebtCollectorLanguageApi.update(stateCode, body),
    {
      refresh: refreshList,
      onSuccess: (updated) => {
        apply(updated);
        toast.success("Saved.");
      },
      onError: (err) => toast.error(errorMessage(err, "Couldn't save. Try again.")),
    },
  );

  const approve = useSubmittingMutation(
    () => tpsDebtCollectorLanguageApi.approve(stateCode),
    {
      refresh: refreshList,
      onSuccess: (updated) => {
        apply(updated);
        toast.success("Approved.");
      },
      onError: (err) => toast.error(errorMessage(err, "Couldn't approve. Try again.")),
    },
  );

  const backButton = (
    <Button type="button" variant="text" size="sm" onClick={onBack} data-testid="tps-dcl-back">
      <ArrowLeft size={16} aria-hidden />
      Back to states
    </Button>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <DetailSection>
          <SkeletonText lines={6} />
        </DetailSection>
      </div>
    );
  }

  const pending = save.pending || approve.pending;
  const dirty = body.trim() !== row.bodyText.trim();
  const blank = body.trim().length === 0;
  // Approve acts on the SAVED draft; require saving first if there are
  // unsaved edits, and only a Draft can be approved.
  const canApprove = row.status === "Draft" && !dirty && !blank;

  return (
    <section
      aria-label={`Debt-collector language for ${stateLabel(stateCode)}`}
      className="flex flex-col gap-space-4"
    >
      <div className="flex items-center justify-between gap-space-3">
        {backButton}
        <StatusBadge tone={STATUS_TONE[row.status]}>{row.status}</StatusBadge>
      </div>

      <div>
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">
          {stateLabel(stateCode)}
        </h2>
        <p className="mt-space-1 text-small text-gray-mid">
          Version {row.version}
          {row.isApproved && row.approvedAt && (
            <>
              {" · Approved "}
              <Timestamp value={row.approvedAt} />
            </>
          )}
        </p>
      </div>

      {/* FE-only — prominent warning above the editor; never stored/printed.
          Only a Draft needs the counsel warning; once approved the badge +
          approved hint convey the state. */}
      {row.status === "Draft" && (
        <div
          role="note"
          data-testid="tps-dcl-banner"
          className="flex items-start gap-space-2 rounded-md border border-status-warning bg-status-warning/10 p-space-3"
        >
          <AlertTriangle size={18} className="mt-[2px] shrink-0 text-status-warning" aria-hidden />
          <p className="m-0 text-small font-semibold text-onyx">{EDITOR_BANNER}</p>
        </div>
      )}

      <DetailSection title="Disclosure">
        <div className="flex flex-col gap-space-3">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={16}
            disabled={pending}
            invalid={blank}
            aria-label="Debt-collector disclosure body"
            data-testid="tps-dcl-body"
          />

          <div className="flex flex-wrap items-center gap-space-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void save.submit()}
              disabled={pending || !dirty || blank}
              loading={save.pending}
              data-testid="tps-dcl-save"
            >
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void approve.submit()}
              disabled={pending || !canApprove}
              loading={approve.pending}
              data-testid="tps-dcl-approve"
            >
              Approve
            </Button>
            {row.status === "Draft" && dirty && (
              <span className="text-caption text-gray-mid" data-testid="tps-dcl-approve-hint">
                Save your changes before approving.
              </span>
            )}
            {row.status === "Approved" && !dirty && (
              <span className="text-caption text-gray-mid">
                This disclosure is approved. Editing it creates a new draft to re-approve.
              </span>
            )}
          </div>
        </div>
      </DetailSection>
    </section>
  );
}
